import { useTranslation } from "react-i18next";
import { Filters, FilterType } from "../../../assets/Services/wsSearch";
import { useMemo, useState, type SetStateAction, type Dispatch } from "react";
import Switch from "../../../assets/TakyonDesignSystem/components/Switch/Switch";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import { filter_accessible_facilities } from "./FilterTags";
import { Accordion } from "react-bootstrap";
import { closeModal } from "../../../assets/Utils/modal";
import { useSearch } from "../../hooks/useSearch";
import imageBadgePlus from "../../../assets/img/directsearch/badge_plus.avif";
import petFriendly from "../../../assets/img/directsearch/pet_friendly.png";
import imageBadgeDiscountLoyalty from "../../../assets/img/directsearch/badge_discount_loyalty.avif";
import { SortHotelsBy } from "../../../assets/Services/wsSearch";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../components/RadioGroup/RadioGroup";
import IconStar from "../../../assets/icons/IconStar";
import { isEqual, difference } from "lodash";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/userSlice";
import { ANALYTICS_SOURCE } from "../../../config/analytics";

type FiltersToApply = {
  certifiedOnly: boolean | undefined;
  isLoyaltyEnabled: boolean | undefined;
  onlyHotels: boolean | undefined;
  minReviewsScore: string | undefined;
  stars: number[] | undefined;
  facilities: string[] | undefined;
};

function PillsFilter({
  title,
  filtersToApply,
  setFiltersToApply,
  filterList,
  filterKey,
}: {
  title?: string;
  filtersToApply: FiltersToApply;
  setFiltersToApply: Dispatch<SetStateAction<FiltersToApply>>;
  filterList: Record<string, string>;
  filterKey: FilterType;
}) {
  const { t } = useTranslation();

  return (
    <>
      {title && <p className="text-base font-medium">{t(title)}</p>}
      <div className="flex gap-2 flex-wrap mt-4 justify-center">
        {Object.entries(filterList).map(([value, type]) => (
          <button
            key={value}
            type="button"
            data-active={(
              filtersToApply[
                filterKey as keyof FiltersToApply
              ] as FiltersToApply["facilities"]
            )?.includes(value)}
            className="bg-transparent px-3.5 h-[2.375rem] text-sm font-medium border border-[#E8E8E8] data-[active=true]:border-black rounded-full text-black"
            onClick={() => {
              setFiltersToApply((prev) => {
                if (prev.facilities === undefined) {
                  return { ...prev, facilities: [value] };
                }

                if (prev.facilities?.includes(value)) {
                  const newFacilities = prev.facilities?.filter(
                    (facility) => facility !== value,
                  );

                  return {
                    ...prev,
                    facilities:
                      newFacilities.length > 0 ? newFacilities : undefined,
                  };
                }

                if (!prev.facilities?.includes(value)) {
                  return {
                    ...prev,
                    facilities: [...(prev.facilities || []), value],
                  };
                }

                return prev;
              });
            }}
          >
            {t(`${filterKey}.${type}`)}
          </button>
        ))}
      </div>
    </>
  );
}

function FiltersAccordion({
  filtersToApply,
  setFiltersToApply,
}: {
  filtersToApply: FiltersToApply;
  setFiltersToApply: Dispatch<
    SetStateAction<{
      certifiedOnly: boolean | undefined;
      isLoyaltyEnabled: boolean | undefined;
      onlyHotels: boolean | undefined;
      minReviewsScore: string | undefined;
      stars: number[] | undefined;
      facilities: string[] | undefined;
    }>
  >;
}) {
  const { t } = useTranslation();
  const keys = [
    "property_type",
    "class",
    "mealplan",
    "fc",
    "accessible_facilities",
  ];

  /* const defaultActive = useMemo(
    () =>
      Object.entries(filters ?? []).filter(
        ([key, value]) =>
          // either the value is present, or it's an array with length > 0 or all possible values are selected
          keys.includes(key) &&
          !!value &&
          !(typeof value === "object" && (value as any[]).length === 0) &&
          !isAllSelected(key, value),
      ),
    [filters],
  ); */

  return (
    <Accordion className="border-top" style={{ marginTop: "26px" }} alwaysOpen>
      <Accordion.Item eventKey="4">
        <Accordion.Header>{t("ds.filter_visual_ads")}</Accordion.Header>
        <Accordion.Body className="p-0">
          <PillsFilter
            filterList={filter_accessible_facilities}
            filtersToApply={filtersToApply}
            setFiltersToApply={setFiltersToApply}
            filterKey="facilities"
          />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

export default function FiltersModal({
  onFilter,
  source,
}: {
  onFilter(props: { filters: Filters | undefined }): void;
  source: (typeof ANALYTICS_SOURCE)[keyof typeof ANALYTICS_SOURCE];
}) {
  const { t } = useTranslation();

  const user = useSelector(selectUser);

  const {
    setParameters,
    searchParameters: { filters },
    handleSortChange,
  } = useSearch();

  /* const defaultFilters: Filters = {
    minPrice: MIN_PRICE,
    maxPrice: MAX_PRICE,
  }; */

  const sortOptions = [
    { name: t("ds.sort_recommended"), value: SortHotelsBy.RECOMMENDED },
    { name: t("ds.sort_price"), value: SortHotelsBy.PRICE },
    { name: t("ds.sort_distance"), value: SortHotelsBy.DISTANCE_TO_CC },
    { name: t("ds.sort_review_score"), value: SortHotelsBy.REVIEWS },
  ] as const;

  const ratingOptions = [
    { name: t("ds.score_any"), value: "any" },
    { name: t("ds.score_90"), value: "90" },
    { name: t("ds.score_80"), value: "80" },
    { name: t("ds.score_70"), value: "70" },
  ];

  /* const updateFilters = (f: Partial<Filters>) => {
    setParameters((p: SearchParameters) => ({
      ...p,
      filters: { ...p.filters, ...f },
    }));
  }; */

  /* const [sliderMin, setSliderMin] = useState<number>(
    Number(filters?.minPrice?.toFixed(0)) || MIN_PRICE,
  );
  const [sliderMax, setSliderMax] = useState<number>(
    Number(filters?.maxPrice?.toFixed(0)) || MAX_PRICE,
  ); */

  const defaultFilters = {
    certifiedOnly: filters?.certifiedOnly,
    isLoyaltyEnabled: filters?.isLoyaltyEnabled,
    onlyHotels: filters?.onlyHotels,
    minReviewsScore: filters?.minReviewsScore,
    stars: filters?.stars,
    facilities: filters?.facilities,
  } satisfies Partial<Filters>;

  const [filtersToApply, setFiltersToApply] = useState(defaultFilters);

  const numberOfFiltersToApply = Object.keys(filtersToApply).reduce(
    (acc, key) => {
      const typedKey = key as keyof typeof filtersToApply;

      if (
        [
          "certifiedOnly",
          "isLoyaltyEnabled",
          "minReviewsScore",
          "onlyHotels",
        ].includes(key)
      ) {
        if (filtersToApply[typedKey] !== defaultFilters[typedKey]) {
          return acc + 1;
        }

        return acc;
      }

      if (["stars", "facilities"].includes(key)) {
        if (!isEqual(defaultFilters[typedKey], filtersToApply[typedKey])) {
          if (
            !Array.isArray(defaultFilters[typedKey]) &&
            Array.isArray(filtersToApply[typedKey])
          ) {
            return (
              //@ts-ignore
              acc + (filtersToApply[typedKey] as Array<unknown>)?.length ?? 0
            );
          }

          if (
            Array.isArray(defaultFilters[typedKey]) &&
            !Array.isArray(filtersToApply[typedKey])
          ) {
            return acc + 1;
          }

          return (
            acc +
            difference(
              filtersToApply[typedKey] as Array<unknown>,
              defaultFilters[typedKey] as Array<unknown>,
            ).length
          );
        }

        return acc;
      }

      return acc;
    },
    0,
  );

  const numberOfActiveFilters = useMemo(
    () =>
      (filters
        ? Object.keys(filters).reduce((acc, key) => {
            const typedKey = key as keyof typeof filters;

            if (
              [
                "certifiedOnly",
                "isLoyaltyEnabled",
                "minReviewsScore",
                "onlyHotels",
              ].includes(key)
            ) {
              if (filters?.[typedKey] !== undefined) {
                return acc + 1;
              }

              return acc;
            }

            if (["stars", "facilities"].includes(key)) {
              if (filters?.[typedKey] !== undefined) {
                return (
                  //@ts-ignore
                  acc + (filters?.[typedKey] as Array<unknown>)?.length ?? 0
                );
              }

              return acc;
            }

            return acc;
          }, 0)
        : 0) + 0,
    [filters],
  );

  const handleShowResults = () => {
    const { ...filtersToApplyWithoutSortBy } = filtersToApply;

    if (
      Object.values(filtersToApplyWithoutSortBy).some(
        (value) =>
          value !==
          defaultFilters[value as keyof typeof filtersToApplyWithoutSortBy],
      )
    ) {
      setParameters((prev) => ({
        ...prev,
        filters: filtersToApplyWithoutSortBy,
      }));

      fireTagManagerEvent("filter_results", {
        categories_filters: JSON.stringify(filters),
        user_id: user?._id ?? null,
        source,
      });
    }

    onFilter({
      filters: filtersToApplyWithoutSortBy,
    });
  };

  const handleClearFilters = () => {
    const newFilters = {
      certifiedOnly: undefined,
      isLoyaltyEnabled: undefined,
      minReviewsScore: undefined,
      stars: undefined,
      onlyHotels: undefined,
      facilities: undefined,
    };

    const newSortBy = undefined;

    setParameters((prev) => ({
      ...prev,
      filters: newFilters,
    }));

    handleSortChange(newSortBy, source);

    onFilter({
      filters: newFilters,
    });
  };

  return (
    <>
      <div className="relative flex justify-end px-3 items-center box-border border-bottom h-20">
        <h3 className="font-medium text-sm text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
          {numberOfActiveFilters > 0
            ? `${t("ds.filters")} (${numberOfActiveFilters})`
            : t("ds.filters")}
        </h3>

        <button
          type="button"
          onClick={closeModal}
          className="bg-transparent border-none touch-hitbox relative"
        >
          <IconNew icon="close" className="size-5" />
        </button>
      </div>
      <div className="w100 modal-body px-5 py-4">
        <section className="flex flex-col gap-4">
          <h3 className="text-base font-medium">{t("ds.most_used_filters")}</h3>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              data-active={!!filtersToApply.certifiedOnly}
              className="flex flex-col items-center gap-2 bg-transparent border-none group"
              onClick={() => {
                setFiltersToApply((prev) => ({
                  ...prev,
                  certifiedOnly:
                    filtersToApply.certifiedOnly === undefined
                      ? true
                      : undefined,
                }));
              }}
            >
              <div className="size-24 flex items-center justify-center rounded-2xl border border-gray-100 group-data-[active=true]:border-black">
                <img
                  src={imageBadgePlus}
                  role="presentation"
                  className="size-14"
                  alt="plus"
                />
              </div>
              <h4 className="text-xs text-medium text-center text-black">
                {t("ds.most_appreciated_filters.0")}
              </h4>
            </button>
            <button
              type="button"
              data-active={!!filtersToApply.isLoyaltyEnabled}
              className="flex flex-col items-center gap-2 bg-transparent border-none group"
              onClick={() => {
                setFiltersToApply((prev) => ({
                  ...prev,
                  isLoyaltyEnabled:
                    filtersToApply.isLoyaltyEnabled === undefined
                      ? true
                      : undefined,
                }));
              }}
            >
              <div className="size-24 flex items-center justify-center rounded-2xl border border-gray-100 group-data-[active=true]:border-black">
                <img
                  src={imageBadgeDiscountLoyalty}
                  role="presentation"
                  className="size-14"
                  alt="badge"
                />
              </div>
              <h4 className="text-xs text-medium text-center text-black">
                {t("ds.most_appreciated_filters.1")}
              </h4>
            </button>
            <button
              type="button"
              data-active={
                filtersToApply.facilities &&
                filtersToApply.facilities.includes("4")
              }
              className="flex flex-col items-center gap-2 bg-transparent border-none group"
              onClick={() => {
                setFiltersToApply((prev) => {
                  if (!prev.facilities) {
                    return {
                      ...prev,
                      facilities: ["4"],
                    };
                  }
                  if (prev.facilities.includes("4")) {
                    return {
                      ...prev,
                      facilities: prev.facilities.filter((f) => f !== "4"),
                    };
                  }
                  return { ...prev, facilities: [...prev.facilities, "4"] };
                });
              }}
            >
              <div className="size-24 flex items-center justify-center rounded-2xl border border-gray-100 group-data-[active=true]:border-black">
                <img
                  src={petFriendly}
                  role="presentation"
                  className="size-14"
                  alt="badge"
                />
              </div>
              <h4 className="text-xs text-medium text-center text-black">
                {t("ds.most_appreciated_filters.2")}
              </h4>
            </button>
          </div>
        </section>
        <div className="horizontal-divider" />
        <div className="flex justify-between items-center">
          <p>{t("ds.onlyHotels")}</p>
          <Switch
            checked={filtersToApply && filtersToApply.onlyHotels === true}
            onChange={(e) => {
              console.log(e.target.checked);
              setFiltersToApply((p) => ({
                ...p,
                onlyHotels: e.target.checked ? true : undefined,
              }));
            }}
          />
        </div>
        <div className="horizontal-divider" />
        <section className="flex flex-col gap-4">
          <h3 className="text-base font-medium">{t("ds.score_filter")}</h3>
          <RadioGroup
            defaultValue={
              !defaultFilters.minReviewsScore
                ? ratingOptions[0].value
                : defaultFilters.minReviewsScore
            }
            className="flex flex-col gap-4"
          >
            {ratingOptions.map(({ name, value }) => (
              <div key={value} className="flex items-center gap-2">
                <RadioGroupItem
                  value={value}
                  id={value}
                  onClick={() => {
                    setFiltersToApply((prev) => ({
                      ...prev,
                      minReviewsScore: value !== "any" ? value : undefined,
                    }));
                  }}
                />
                <label htmlFor={value}>{name}</label>
              </div>
            ))}
          </RadioGroup>
        </section>
        <div className="horizontal-divider" />
        <section className="flex flex-col gap-4">
          <h3 className="text-base font-medium">
            {t("ds.classification_structure")}
          </h3>
          <div className="flex items-center gap-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <button
                type="button"
                key={index}
                data-active={filtersToApply.stars?.includes(index + 1)}
                className="flex justify-center items-center gap-1.5 rounded-full border border-[#E8E8E8] px-3 py-1.5 flex-1 bg-transparent data-[active=true]:border-black text-black"
                onClick={() => {
                  setFiltersToApply((prev) => {
                    if (prev.stars === undefined) {
                      return { ...prev, stars: [index + 1] };
                    }

                    if (prev.stars?.includes(index + 1)) {
                      const newStars = prev.stars?.filter(
                        (star) => star !== index + 1,
                      );
                      return {
                        ...prev,
                        stars: newStars.length > 0 ? newStars : undefined,
                      };
                    }

                    if (!prev.stars?.includes(index + 1)) {
                      return {
                        ...prev,
                        stars: [...(prev.stars || []), index + 1],
                      };
                    }

                    return prev;
                  });
                }}
              >
                <span className="text-sm font-medium">{index + 1}</span>
                <IconStar className="size-4" />
              </button>
            ))}
          </div>
        </section>
        {/* <div className="horizontal-divider" style={{ margin: "26px 0" }}></dsiv> */}

        {/* <PillsFilter
          title={"ds.facilities"}
          filterList={facility_generals}
          filtersToApply={filtersToApply}
          setFiltersToApply={setFiltersToApply}
          filterKey="facilities"
        /> */}

        <FiltersAccordion
          filtersToApply={filtersToApply}
          setFiltersToApply={setFiltersToApply}
        />
      </div>
      <div>
        <div className="horizontal-divider" style={{ margin: "0" }}></div>
        <div className="d-flex justify-content-between align-items-center px-5 py-3">
          <button
            type="button"
            className="underline text-sm font-medium bg-transparent border-none text-black"
            onClick={handleClearFilters}
          >
            {t("ds.clear_filters")}
          </button>
          <Button
            text={t("ds.show_results_filter")}
            onClick={handleShowResults}
            className="[&>p]:text-sm [&>p]:font-medium"
            disabled={numberOfFiltersToApply === 0}
          />
        </div>
      </div>
    </>
  );
}
